import { Checkbox, Flex, Typography, colors } from '@aloft/toolkit-sui';
import { Box } from '@aloft/toolkit-sui/chakra';
import { ColumnDef, Row } from '@tanstack/react-table';

import { ChangeEvent, Dispatch, SetStateAction } from 'react';

import { CompSelectionProperty, CompSelectionState } from '../../types.ts';

const SELECT_COLUMN_SIZE = 32;
const AIV_ARV_SELECT_COLUMN_SIZE = 88;

const noFocusRingSx = {
  '& .chakra-checkbox__control:focus': { boxShadow: 'none' },
  '& .chakra-checkbox__control:focus-visible': { boxShadow: 'none' },
};

export const makeSelectionColumn = (
  getRowSelection: () => CompSelectionState,
  setRowSelection: Dispatch<SetStateAction<CompSelectionState>>,
  isInspectifyOrder: boolean,
  hasArvOption: boolean,
): ColumnDef<CompSelectionProperty> => {
  if (!isInspectifyOrder || !hasArvOption) {
    return {
      id: 'select',
      size: SELECT_COLUMN_SIZE,
      minSize: SELECT_COLUMN_SIZE,
      maxSize: SELECT_COLUMN_SIZE,
      header: () => null,
      cell: ({ row }: { row: Row<CompSelectionProperty> }) => {
        if (row.id === 'subject') {
          return <Flex height="100%" minWidth={`${SELECT_COLUMN_SIZE}px`} />;
        }

        const rowId = row.id;
        const rowSelection = getRowSelection();
        const isSelected = !!rowSelection[rowId];

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
          const { checked } = e.target;
          setRowSelection((prev) => {
            if (checked) {
              return { ...prev, [rowId]: {} };
            }
            const newSelection = { ...prev };
            delete newSelection[rowId];
            return newSelection;
          });
        };

        return (
          <Flex
            height="100%"
            minWidth={`${SELECT_COLUMN_SIZE}px`}
            alignItems="center"
            justifyContent="center"
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleChange}
                isChecked={isSelected}
                aria-label={`Comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
        );
      },
    };
  }

  return {
    id: 'select',
    size: AIV_ARV_SELECT_COLUMN_SIZE,
    minSize: AIV_ARV_SELECT_COLUMN_SIZE,
    maxSize: AIV_ARV_SELECT_COLUMN_SIZE,
    header: () => (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={1}
        height="100%"
      >
        <Flex gap={4} alignItems="center" justifyContent="center">
          <Flex flexDirection="column" alignItems="center" gap={0.5}>
            <Typography
              color="textOffset"
              __dangerDoNotUseSx={{
                backgroundColor: colors.statusWarning,
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              AIV
            </Typography>
          </Flex>
          <Flex flexDirection="column" alignItems="center" gap={0.5}>
            <Typography
              color="textOffset"
              __dangerDoNotUseSx={{
                backgroundColor: colors.statusComplete,
                padding: '4px 8px',
                borderRadius: '4px',
              }}
            >
              ARV
            </Typography>
          </Flex>
        </Flex>
      </Flex>
    ),
    cell: ({ row }: { row: Row<CompSelectionProperty> }) => {
      if (row.id === 'subject') {
        return <Flex height="100%" minWidth={`${AIV_ARV_SELECT_COLUMN_SIZE}px`} />;
      }

      const rowId = row.id;
      const rowSelection = getRowSelection();
      const selection = rowSelection[rowId] || {};
      const isAivSelected = selection.aiv ?? false;
      const isArvSelected = selection.arv ?? false;

      const handleAivChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setRowSelection((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            aiv: checked,
            arv: checked ? false : (prev[rowId]?.arv ?? false),
          },
        }));
      };

      const handleArvChange = (e: ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setRowSelection((prev) => ({
          ...prev,
          [rowId]: {
            ...prev[rowId],
            arv: checked,
            aiv: checked ? false : (prev[rowId]?.aiv ?? false),
          },
        }));
      };

      return (
        <Flex
          height="100%"
          minWidth={`${AIV_ARV_SELECT_COLUMN_SIZE}px`}
          alignItems="center"
          justifyContent="space-between"
          onClick={(e) => e.stopPropagation()}
        >
          <Flex flexDirection="column" paddingLeft={4} alignItems="center" gap={0.5}>
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleAivChange}
                isChecked={isAivSelected}
                aria-label={`AIV comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
          <Flex flexDirection="column" paddingRight={4} alignItems="center" gap={0.5}>
            <Box sx={noFocusRingSx}>
              <Checkbox
                onChange={handleArvChange}
                isChecked={isArvSelected}
                aria-label={`ARV comp selection for ${rowId}`}
              />
            </Box>
          </Flex>
        </Flex>
      );
    },
  };
};
